import { useMutation, useQueryClient } from '@tanstack/react-query'
import { CompanyDetail } from 'api/company/companyDetail'
import { editGeneralInfo } from 'api/company/editGeneralInfo'
import { companyDetailKey } from 'queries/company/keys'

export const useEditGeneralDetailsMutation = (companyId: number) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (
      request: Pick<
        Parameters<typeof editGeneralInfo>[1],
        | 'type'
        | 'requiresEverify'
        | 'terminalTierThresholdHours'
        | 'terminalTierThresholdDirection'
        | 'pastShiftCreationAllowed'
      >
    ) => editGeneralInfo(companyId, request),
    onMutate: async ({
      type,
      requiresEverify,
      terminalTierThresholdHours,
      terminalTierThresholdDirection,
      pastShiftCreationAllowed,
    }) => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: companyDetailKey(companyId) })

      const previousCompanyDetail = queryClient.getQueryData(
        companyDetailKey(companyId)
      )

      // Optimistically update to the new value
      queryClient.setQueryData<CompanyDetail>(
        companyDetailKey(companyId),
        (old) => {
          if (!old) return old
          return {
            ...old,
            type: type ?? old.type,
            requiresEverify: requiresEverify ?? old.requiresEverify,
            terminalTierThresholdHours:
              terminalTierThresholdHours ?? old.terminalTierThresholdHours,
            terminalTierThresholdDirection:
              terminalTierThresholdDirection ??
              old.terminalTierThresholdDirection,
            pastShiftCreationAllowed:
              pastShiftCreationAllowed ?? old.pastShiftCreationAllowed,
          }
        }
      )

      return { previousCompanyDetail }
    },
    onError: (error, _, context) => {
      queryClient.setQueryData(
        companyDetailKey(companyId),
        context?.previousCompanyDetail
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: companyDetailKey(companyId) })
    },
  })
}
